import { useEffect } from 'react'

import { useQuery } from '@percent/utility'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { ErrorView, Loader } from '@percent/admin-dashboard/common/components'
import { useAuthState, useRequiredParams } from '@percent/admin-dashboard/common/hooks'
import { usePermissions } from '@percent/admin-dashboard/common/hooks/usePermissions/usePermissions'
import {
  useAssignedDomains,
  useEffectiveRoles,
  usePermissionMutations
} from '@percent/admin-dashboard/app/accounts/hooks'
import { useBreadcrumbsData } from '@percent/admin-dashboard/app/Layout/breadcrumbsContext/useBreadcrumbsData/useBreadcrumbsData'
import { PartnerScopes } from '@percent/admin-dashboard/constants/scopes'
import { MemberProfile } from './MemberProfile'
import type { MemberProfileContainerProps, PermissionsData } from './MemberProfile.types'

export function MemberProfileContainer({ memberType }: MemberProfileContainerProps) {
  const { accountId, id } = useRequiredParams<{ accountId: string; id?: string }>()
  const { setPath } = useBreadcrumbsData()
  const { iamService, adminService } = useServices()
  const { user } = useAuthState()
  const { userHasPermissions } = usePermissions()

  const {
    data: account,
    isLoading,
    errorMessage,
    refetch
  } = useQuery([`/accounts/${accountId}`], iamService.getAccountDetail, { accountId })

  const partnerId = account?.partnerId
  const organisationId = memberType === 'organisation' ? id || account?.organisationId : account?.organisationId

  const { data: partnerData } = useQuery(
    [`/partners/${partnerId}`],
    adminService.queries.getPartnerDetail,
    { id: partnerId },
    { enabled: !!partnerId }
  )

  // A strict-SSO partner's members authenticate via SSO, so resending an
  // invitation is meaningless — the partner members list hides it for them. The
  // SSO scope lives behind its own endpoint/permission (not in the partner
  // detail), so fetch it only for partner profiles; when we can't read it we fall
  // back to "not strict" (resend stays available), matching the prior behaviour.
  const canListPartnerScopes = userHasPermissions('partners:can_list_scopes')

  const { data: partnerScopeData } = useQuery(
    [`/partners/${partnerId}/scopes`],
    adminService.queries.getPartnerScopeList,
    // `partnerId` is required by the endpoint; the empty-string fallback is never
    // sent because `enabled` already requires a truthy partnerId.
    { partnerId: partnerId ?? '' },
    { enabled: memberType === 'partner' && !!partnerId && canListPartnerScopes }
  )

  const isStrictSSOPartner = !!partnerScopeData?.some(({ scopeName }) => scopeName === PartnerScopes.SingleSignOn)

  const { data: orgData } = useQuery(
    [`/organisations/${organisationId}`],
    adminService.getOrganisation,
    { id: organisationId },
    { enabled: !!organisationId }
  )

  const isGoodstack = memberType === 'goodstack'

  const domains = useAssignedDomains('accounts', accountId, isGoodstack)

  const effectiveRoles = useEffectiveRoles('accounts', isGoodstack ? accountId : undefined)

  const { assignRole, unassignRole } = usePermissionMutations(
    iamService.assignAccountRole,
    iamService.unassignAccountRole,
    domains.refresh,
    () => {
      domains.markChanged()
      effectiveRoles.refresh()
    }
  )

  const permissions: PermissionsData | undefined = isGoodstack
    ? {
        assignedDomains: domains.assignedDomains ?? [],
        effectiveDomains: effectiveRoles.effectiveDomains,
        getGroupHref: effectiveRoles.getGroupHref,
        isLoading: domains.isLoading,
        // Per-domain edit-ability is handled by canSetRoles on each assigned
        // domain (PermissionsList); only guard against editing your own roles.
        isDisabled: user?.id === accountId,
        assignAccountRole: async (roleId: string, domainId: string) =>
          assignRole({ memberId: accountId, role: roleId, domainId }),
        unassignAccountRole: async (roleId: string, domainId: string) =>
          unassignRole({ memberId: accountId, role: roleId, domainId })
      }
    : undefined

  const canChangeRole = memberType === 'partner' && userHasPermissions('partner_accounts:can_set_legacy_role')

  const partnerName = partnerData?.name
  const organisationName = orgData?.name
  const entityName = memberType === 'partner' ? partnerName : organisationName || partnerName
  const displayName = account?.fullName || account?.email

  useEffect(() => {
    if (memberType === 'goodstack') {
      setPath(displayName)
    } else {
      setPath(entityName && displayName ? `${entityName}|${displayName}` : displayName)
    }
  }, [displayName, entityName, memberType, setPath])

  if (isLoading) {
    return <Loader />
  }

  if (errorMessage || !account) {
    return <ErrorView errorMessage={errorMessage || 'Account not found'} />
  }

  return (
    <MemberProfile
      account={account}
      memberType={memberType}
      partnerName={partnerName}
      organisationName={organisationName}
      permissions={permissions}
      effectiveRoles={
        isGoodstack && effectiveRoles.isEnabled
          ? {
              effectiveDomains: effectiveRoles.effectiveDomains ?? [],
              isLoading: effectiveRoles.isLoading,
              errorMessage: effectiveRoles.errorMessage,
              getGroupHref: effectiveRoles.getGroupHref
            }
          : undefined
      }
      canChangeRole={canChangeRole}
      isStrictSSOPartner={isStrictSSOPartner}
      onRefresh={refetch}
    />
  )
}
